import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  TextField,
  Box,
  Button,
  Snackbar,
  CircularProgress,
  MenuItem,
  Chip
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { useApi } from '../api/useApi';

export default function UpdateProfileDialog({ open, onClose, user, onUpdated }) {
  const { authFetch } = useApi();

  // --- Profile state ---
  const [formValues, setFormValues] = useState({
    id: null, email: '', firstName: '', lastName: '', phone: '', whatsappPhone: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // --- Favorites state ---
  const [allMakes, setAllMakes] = useState([]);                 // Set<Make>
  const [userFavRecords, setUserFavRecords] = useState([]);     // [{id, make}]
  const [selectedMakes, setSelectedMakes] = useState([]);       // [string]
  const [savingFavs, setSavingFavs] = useState(false);

  // --- UI state ---
  const [tab, setTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Sync profile when `user` changes
  useEffect(() => {
    if (!user) return;
    setFormValues({
      id: user.id,
      email: user.email || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      phone: user.phone || '',
      whatsappPhone: user.whatsappPhone || ''
    });
  }, [user]);

  // When user opens Favorites tab, load both allMakes and current favorites
  useEffect(() => {
    if (!(open && tab === 1 && user)) return;
    (async () => {
      try {
        const [makesRes, favRes] = await Promise.all([
          authFetch(`/user/favorite/make`),
          authFetch(`/user/favorite/${user.id}`)
        ]);
        if (!makesRes.ok || !favRes.ok) throw new Error();
        const [makes, favs] = await Promise.all([makesRes.json(), favRes.json()]);
        setAllMakes(makes);
        // keep only non-deleted, store full records, and extract string names
        const active = favs.filter(f => !f.deleted);
        setUserFavRecords(active);
        setSelectedMakes(active.map(f => f.make));
      } catch {
        showSnackbar('Failed to load favorites', 'error');
      }
    })();
  }, [open, tab, user, authFetch]);

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });
  const handleSnackbarClose = () =>
    setSnackbar(s => ({ ...s, open: false }));

  const handleChange = e => {
    const { name, value } = e.target;
    setFormValues(f => ({ ...f, [name]: value }));
  };

  // Save Profile
  const handleProfileSave = async () => {
    setSavingProfile(true);
    try {
      const res = await authFetch(`/user/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formValues)
      });
      if (!res.ok) throw new Error(await res.text());
      const updated = await res.json();
      showSnackbar('Profile updated', 'success');
      onUpdated(updated);
      onClose();
    } catch {
      showSnackbar('Failed to update profile', 'error');
    } finally {
      setSavingProfile(false);
    }
  };

  // Save Favorites: figure out adds vs deletes
  const handleFavsSave = async () => {
    setSavingFavs(true);
    try {
      // 1. Deletions: records whose make was unselected
      const toDelete = userFavRecords
        .filter(rec => !selectedMakes.includes(rec.make))
        .map(rec => rec.id);

      if (toDelete.length) {
        const delRes = await authFetch(`/user/favorite`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userFavoriteIds: toDelete })
        });
        if (!delRes.ok) throw new Error(await delRes.text());
      }

      // 2. Creations: makes newly selected that weren’t in userFavRecords
      const existingMakes = userFavRecords.map(rec => rec.make);
      const toAdd = selectedMakes
        .filter(mk => !existingMakes.includes(mk))
        .map(mk => ({ make: mk }));

      if (toAdd.length) {
        const payload = { userId: user.id, favorites: toAdd };
        const addRes = await authFetch(`/user/favorite/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!addRes.ok) throw new Error(await addRes.text());
      }

      showSnackbar('Favorites updated', 'success');
      onClose();
    } catch {
      showSnackbar('Failed to update favorites', 'error');
    } finally {
      setSavingFavs(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="Profile" />
            <Tab label="Favorites" />
          </Tabs>
        </DialogTitle>

        <DialogContent dividers>
          {tab === 0 ? (
            <Box component="form" noValidate sx={{ display: 'grid', gap: 2 }}>
              <TextField
                margin="dense"
                label="First Name"
                name="firstName"
                fullWidth
                value={formValues.firstName}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                label="Last Name"
                name="lastName"
                fullWidth
                value={formValues.lastName}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                label="Email"
                name="email"
                type="email"
                fullWidth
                value={formValues.email}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                label="Phone"
                name="phone"
                fullWidth
                value={formValues.phone}
                onChange={handleChange}
              />
              <TextField
                margin="dense"
                label="WhatsApp"
                name="whatsappPhone"
                fullWidth
                value={formValues.whatsappPhone}
                onChange={handleChange}
              />
            </Box>
          ) : (
            <>
              {allMakes.length === 0 ? (
                <Box display="flex" justifyContent="center" py={5}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <TextField
                    select
                    label="Favourite Brands"
                    fullWidth
                    margin="dense"
                    value={selectedMakes}
                    onChange={e => setSelectedMakes(e.target.value)}
                    SelectProps={{
                      multiple: true,
                      renderValue: sel => sel.join(', ')
                    }}
                  >
                    {allMakes.map(m => (
                      <MenuItem key={m.id} value={m.name}>
                        {m.name}
                      </MenuItem>
                    ))}
                  </TextField>

                  <Box
                    sx={{
                      mt: 1,
                      p: 1,
                      minHeight: 48,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      bgcolor: 'background.paper'
                    }}
                  >
                    {selectedMakes.map(makeName => (
                      <Chip key={makeName} label={makeName} />
                    ))}
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={savingProfile || savingFavs}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={tab === 0 ? handleProfileSave : handleFavsSave}
            disabled={tab === 0 ? savingProfile : savingFavs}
          >
            {tab === 0
              ? (savingProfile ? 'Saving…' : 'Save Profile')
              : (savingFavs ? 'Saving…' : 'Save Favourites')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MuiAlert severity={snackbar.severity} onClose={handleSnackbarClose}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
}